<div class="min-h-screen bg-gray-50">
  <!-- Modern Responsive Header -->
  <div class="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
      <!-- Top Row: Title & Actions -->
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        
        <!-- Header Title -->
        <div class="flex items-center gap-4">
          <!-- Clean, modern enterprise icon design -->
          <div class="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center border border-blue-100 shadow-sm">
            <i class="fa fa-chart-line text-xl"></i>
          </div>
          <div class="flex flex-col justify-center">
            <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-800 tracking-tight leading-none mb-1">
              Tableau de Bord Financier
            </h1>
            <p class="text-sm font-medium text-slate-500 flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
              Rapports et analyses en temps réel
            </p>
          </div>
        </div>

        <!-- Filters & Actions -->
        <div class="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full lg:w-auto">
          
          <!-- Period Selector Group -->
          <div class="flex-1 sm:flex-none bg-gray-50/80 p-1.5 rounded-xl border border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shadow-inner">
            
            <!-- Quick Pick dropdown -->
            <div class="relative w-full sm:w-40">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="fa fa-calendar-alt text-blue-500 text-sm"></i>
              </div>
              <select (change)="onPeriodSelect($event)"
                class="w-full pl-9 pr-8 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 appearance-none cursor-pointer shadow-sm hover:border-gray-300 transition-colors">
                <option value="today">Aujourd'hui</option>
                <option value="week">7 Derniers Jours</option>
                <option value="month" selected>Ce Mois</option>
                <option value="lastMonth">Mois Précédent</option>
              </select>
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <i class="fa fa-chevron-down text-gray-400 text-xs"></i>
              </div>
            </div>

            <div class="hidden sm:block w-px h-8 bg-gray-200"></div>

            <!-- Custom Date Range -->
            <div class="flex items-center gap-2 w-full sm:w-auto">
              <input type="date" [(ngModel)]="dateRange.startDate" (change)="onDateRangeChange()"
                class="flex-1 sm:flex-none w-full sm:w-36 px-3 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 shadow-sm transition-colors cursor-pointer">
              
              <span class="text-gray-400 font-bold px-1 text-xs uppercase">à</span>
              
              <input type="date" [(ngModel)]="dateRange.endDate" (change)="onDateRangeChange()"
                class="flex-1 sm:flex-none w-full sm:w-36 px-3 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 shadow-sm transition-colors cursor-pointer">
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-2 w-full sm:w-auto">
            <button type="button" (click)="refreshData()" [disabled]="isLoading"
              class="flex-1 sm:flex-none px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/20 disabled:opacity-70 disabled:cursor-not-allowed transition-all shadow-md shadow-blue-500/20 flex items-center justify-center gap-2 whitespace-nowrap">
              <i class="fa text-base" [class.fa-spinner]="isLoading" [class.fa-sync-alt]="!isLoading"
                [class.fa-spin]="isLoading"></i>
              <span>{{ isLoading ? 'Actualisation...' : 'Actualiser' }}</span>
            </button>

            <button *ngIf="hasApiError" type="button" (click)="retryApiConnection()"
              class="flex-1 sm:flex-none px-4 py-2.5 text-red-600 bg-red-50 text-sm border border-red-200 font-bold rounded-xl hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-4 focus:ring-red-500/20 transition-all flex items-center justify-center gap-2 shadow-sm whitespace-nowrap">
              <i class="fa fa-wifi"></i>
              <span>Réessayer</span>
            </button>
          </div>
          
        </div>
      </div>
    </div>
  </div>

  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <!-- Loading State -->
    <div *ngIf="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <i class="fa fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
        <p class="text-gray-600">Chargement des données financières...</p>
      </div>
    </div>

    <!-- Error State -->
    <div *ngIf="error && !isLoading" class="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
      <div class="flex items-center gap-3">
        <i class="fa fa-exclamation-triangle text-red-600"></i>
        <div>
          <h3 class="text-lg font-semibold text-red-800">Erreur de Connexion</h3>
          <p class="text-red-700">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div *ngIf="!isLoading && !error">
      <!-- No Data Layout -->
      <div *ngIf="!hasData()" class="min-h-[60vh] flex items-center justify-center">
        <div class="max-w-2xl mx-auto text-center px-6">
          <!-- API Error State -->
          <div *ngIf="hasApiError" class="space-y-6">
            <div class="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <i class="fa fa-wifi text-red-500 text-3xl"></i>
            </div>
            <div class="space-y-4">
              <h3 class="text-2xl font-bold text-gray-900">
                Connexion API Indisponible
              </h3>
              <p class="text-gray-600 text-lg leading-relaxed">
                Impossible de se connecter au serveur backend. Vérifiez que le serveur est démarré et accessible.
              </p>
              <div class="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
                <div class="flex items-center gap-3">
                  <i class="fa fa-info-circle text-red-600"></i>
                  <div class="text-left">
                    <p class="text-sm font-medium text-red-800">Détails techniques</p>
                    <p class="text-xs text-red-700 mt-1">Endpoint: {{ environment.apiUrl }}/reports/daily</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <button type="button" (click)="retryApiConnection()"
                class="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center justify-center gap-3 font-medium">
                <i class="fa fa-wifi"></i>
                Réessayer la Connexion
              </button>
              <button type="button" (click)="refreshData()"
                class="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center gap-3 font-medium">
                <i class="fa fa-sync-alt"></i>
                Actualiser
              </button>
            </div>
          </div>

          <!-- No Data for Period State -->
          <div *ngIf="!hasApiError" class="space-y-6">
            <div class="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <i class="fa fa-chart-bar text-blue-500 text-3xl"></i>
            </div>
            <div class="space-y-4">
              <h3 class="text-2xl font-bold text-gray-900">
                Aucune Donnée Financière
              </h3>
              <p class="text-gray-600 text-lg leading-relaxed">
                Aucune statistique financière disponible pour la période sélectionnée.
                Essayez de changer la période ou vérifiez qu'il y a des transactions dans votre système.
              </p>
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                <div class="flex items-center gap-3">
                  <i class="fa fa-calendar text-blue-600"></i>
                  <div class="text-left">
                    <p class="text-sm font-medium text-blue-800">Période actuelle</p>
                    <p class="text-xs text-blue-700 mt-1">{{ dateRange.startDate }} - {{ dateRange.endDate }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <button type="button" (click)="setPeriod('month')"
                class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center gap-3 font-medium">
                <i class="fa fa-calendar"></i>
                Ce Mois
              </button>
              <button type="button" (click)="setPeriod('week')"
                class="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center justify-center gap-3 font-medium">
                <i class="fa fa-calendar-week"></i>
                7 Jours
              </button>
              <button type="button" (click)="setPeriod('today')"
                class="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 flex items-center justify-center gap-3 font-medium">
                <i class="fa fa-calendar-day"></i>
                Aujourd'hui
              </button>
            </div>
          </div>

          <!-- Help Section -->
          <div class="mt-12 pt-8 border-t border-gray-200">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div class="bg-gray-50 rounded-lg p-4">
                <div class="flex items-center gap-3 mb-3">
                  <i class="fa fa-lightbulb text-yellow-600"></i>
                  <h4 class="font-semibold text-gray-900">Conseils</h4>
                </div>
                <ul class="text-sm text-gray-600 space-y-2">
                  <li class="flex items-start gap-2">
                    <i class="fa fa-check text-green-500 text-xs mt-1"></i>
                    Vérifiez que des transactions existent dans votre système
                  </li>
                  <li class="flex items-start gap-2">
                    <i class="fa fa-check text-green-500 text-xs mt-1"></i>
                    Assurez-vous que les dates sélectionnées sont correctes
                  </li>
                  <li class="flex items-start gap-2">
                    <i class="fa fa-check text-green-500 text-xs mt-1"></i>
                    Contactez l'administrateur si le problème persiste
                  </li>
                </ul>
              </div>
              <div class="bg-gray-50 rounded-lg p-4">
                <div class="flex items-center gap-3 mb-3">
                  <i class="fa fa-cog text-blue-600"></i>
                  <h4 class="font-semibold text-gray-900">Actions</h4>
                </div>
                <ul class="text-sm text-gray-600 space-y-2">
                  <li class="flex items-start gap-2">
                    <i class="fa fa-arrow-right text-blue-500 text-xs mt-1"></i>
                    Changez la période de temps
                  </li>
                  <li class="flex items-start gap-2">
                    <i class="fa fa-arrow-right text-blue-500 text-xs mt-1"></i>
                    Actualisez les données
                  </li>
                  <li class="flex items-start gap-2">
                    <i class="fa fa-arrow-right text-blue-500 text-xs mt-1"></i>
                    Vérifiez la connexion réseau
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Dashboard Content with Data -->
      <div *ngIf="hasData()">
        <!-- Aggregated Data Unavailable Warning -->
        <div *ngIf="!aggregatedData && !hasApiError" class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div class="flex items-center gap-3">
            <i class="fa fa-exclamation-triangle text-yellow-600"></i>
            <div>
              <h3 class="text-sm font-semibold text-yellow-800">Données Partielles</h3>
              <p class="text-yellow-700 text-sm">
                Les données agrégées ne sont pas disponibles. Seules les données quotidiennes sont affichées.
              </p>
            </div>
          </div>
        </div>

        <!-- Summary Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <!-- Raw Benefit -->
          <div
            class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300 relative group">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Bénéfice Brut</p>
                <p class="text-2xl font-bold text-emerald-500"
                  [title]="totals.totalRawPaidBenefit ? totals.totalRawPaidBenefit.toLocaleString() + ' MAD' : '0 MAD'">
                  {{ formatCurrency(totals.totalRawPaidBenefit) }}</p>
              </div>
              <div
                class="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 rounded-full flex items-center justify-center border-2 border-emerald-500/30">
                <i class="fa fa-plus-circle text-emerald-600 text-xl"></i>
              </div>
            </div>
            <div class="absolute bottom-2 left-6 text-[10px] text-gray-400">Profit total généré avant couverture des
              dettes</div>
          </div>

          <!-- Net Benefit -->
          <div
            class="bg-white rounded-xl shadow-sm p-6 border border-blue-100 hover:shadow-lg transition-shadow duration-300 relative group ring-1 ring-blue-50">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Bénéfice Net (Réel)</p>
                <p class="text-2xl font-bold text-blue-600"
                  [title]="totals.totalPaidBenefit ? totals.totalPaidBenefit.toLocaleString() + ' MAD' : '0 MAD'">{{
                  formatCurrency(totals.totalPaidBenefit) }}</p>
              </div>
              <div
                class="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-full flex items-center justify-center border-2 border-blue-500/30">
                <i class="fa fa-check-double text-blue-600 text-xl"></i>
              </div>
            </div>
            <div class="absolute bottom-2 left-6 text-[10px] text-blue-400 font-medium">Profit restant après couverture
              des impayés
            </div>
          </div>

          <!-- Unpaid Benefit -->
          <div
            class="bg-white rounded-xl shadow-sm p-6 border border-amber-100 hover:shadow-lg transition-shadow duration-300 relative group ring-1 ring-amber-50">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Bénéfice Impayé</p>
                <p class="text-2xl font-bold text-amber-600"
                  [title]="totals.totalUnpaidBenefit ? totals.totalUnpaidBenefit.toLocaleString() + ' MAD' : '0 MAD'">{{
                  formatCurrency(totals.totalUnpaidBenefit) }}</p>
              </div>
              <div
                class="w-12 h-12 bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-full flex items-center justify-center border-2 border-amber-500/30">
                <i class="fa fa-hand-holding-usd text-amber-600 text-xl"></i>
              </div>
            </div>
            <div class="absolute bottom-2 left-6 text-[10px] text-amber-400 font-medium">Part du bénéfice sur commandes
              non encore réglées</div>
          </div>

          <!-- Remaining Risk -->
          <div
            class="bg-white rounded-xl shadow-sm p-6 border border-red-100 hover:shadow-lg transition-shadow duration-300 relative group ring-1 ring-red-50">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Capital à Risque</p>
                <p class="text-2xl font-bold text-red-600"
                  [title]="totals.totalRemainingUnpaid ? totals.totalRemainingUnpaid.toLocaleString() + ' MAD' : '0 MAD'">
                  {{ formatCurrency(totals.totalRemainingUnpaid) }}</p>
              </div>
              <div
                class="w-12 h-12 bg-gradient-to-br from-red-500/20 to-red-600/20 rounded-full flex items-center justify-center border-2 border-red-500/30">
                <i class="fa fa-shield-alt text-red-600 text-xl"></i>
              </div>
            </div>
            <div class="absolute bottom-2 left-6 text-[10px] text-red-400 font-medium">Montant total encore dû après
              toutes les absorptions
            </div>
          </div>
        </div>

        <!-- Charts Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <!-- Cash Flow Trend -->
          <div
            class="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
            <div class="flex items-center gap-3 mb-6">
              <div
                class="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-full flex items-center justify-center border-2 border-blue-500/30">
                <i class="fa fa-chart-line text-blue-600 text-sm"></i>
              </div>
              <div class="flex flex-col justify-center">
                <h3 class="text-base font-bold text-gray-900 leading-none mb-0">Flux de Trésorerie</h3>
                <p class="text-xs text-gray-400 mt-0.5">Cash encaissé vs avoirs remboursés</p>
              </div>
            </div>
            <div class="h-80">
              <canvas baseChart [data]="cashFlowChart.data" [options]="cashFlowChart.options"
                [type]="cashFlowChart.type">
              </canvas>
            </div>
          </div>

          <!-- Benefit Analysis -->
          <div
            class="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
            <div class="flex items-center gap-3 mb-6">
              <div
                class="w-10 h-10 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 rounded-full flex items-center justify-center border-2 border-emerald-500/30">
                <i class="fa fa-layer-group text-emerald-600 text-sm"></i>
              </div>
              <div class="flex flex-col justify-center">
                <h3 class="text-base font-bold text-gray-900 leading-none mb-0">Rentabilité &amp; Absorption</h3>
                <p class="text-xs text-gray-400 mt-0.5">Bénéfice brut, net, et impayé journalier</p>
              </div>
            </div>
            <div class="h-80">
              <canvas baseChart [data]="benefitAnalysisChart.data" [options]="benefitAnalysisChart.options"
                [type]="benefitAnalysisChart.type">
              </canvas>
            </div>
          </div>

          <!-- Paid vs Unpaid Benefit Analysis -->
          <div
            class="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
            <div class="flex items-center gap-3 mb-6">
              <div
                class="w-10 h-10 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-full flex items-center justify-center border-2 border-green-500/30">
                <i class="fa fa-balance-scale text-green-600 text-sm"></i>
              </div>
              <div class="flex flex-col justify-center">
                <h3 class="text-base font-bold text-gray-900 leading-none mb-0">État des Bénéfices (Payés & Impayés)</h3>
                <p class="text-xs text-gray-400 mt-0.5">Marge collectée ou en attente</p>
              </div>
            </div>
            <div class="h-80">
              <canvas baseChart [data]="paidVsUnpaidBenefitChart.data" [options]="paidVsUnpaidBenefitChart.options"
                [type]="paidVsUnpaidBenefitChart.type">
              </canvas>
            </div>
          </div>

          <!-- Paid vs Unpaid Capital Analysis -->
          <div
            class="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
            <div class="flex items-center gap-3 mb-6">
              <div
                class="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-full flex items-center justify-center border-2 border-purple-500/30">
                <i class="fa fa-boxes text-purple-600 text-sm"></i>
              </div>
              <div class="flex flex-col justify-center">
                <h3 class="text-base font-bold text-gray-900 leading-none mb-0">Capital Recouvré vs Non Recouvré</h3>
                <p class="text-xs text-gray-400 mt-0.5">Marchandises encaissées vs en attente de paiement</p>
              </div>
            </div>
            <div class="h-80">
              <canvas baseChart [data]="paidVsUnpaidCapitalChart.data" [options]="paidVsUnpaidCapitalChart.options"
                [type]="paidVsUnpaidCapitalChart.type">
              </canvas>
            </div>
          </div>


          <!-- Revenue Distribution -->
          <div
            class="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
            <div class="flex items-center gap-3 mb-6">
              <div
                class="w-10 h-10 bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 rounded-full flex items-center justify-center border-2 border-cyan-500/30">
                <i class="fa fa-coins text-cyan-600 text-sm"></i>
              </div>
              <div class="flex flex-col justify-center">
                <h3 class="text-base font-bold text-gray-900 leading-none mb-0">Répartition des Revenus</h3>
                <p class="text-xs text-gray-400 mt-0.5">Trésorerie collectée, créances clients et prêts impayés</p>
              </div>
            </div>
            <div class="h-80">
              <canvas baseChart [data]="revenueDistributionChart.data" [options]="revenueDistributionChart.options"
                [type]="revenueDistributionChart.type">
              </canvas>
            </div>
          </div>

          <!-- Capital Distribution -->
          <div
            class="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
            <div class="flex items-center gap-3 mb-6">
              <div
                class="w-10 h-10 bg-gradient-to-br from-indigo-500/20 to-violet-600/20 rounded-full flex items-center justify-center border-2 border-indigo-500/30">
                <i class="fa fa-chart-pie text-indigo-600 text-sm"></i>
              </div>
              <div class="flex flex-col justify-center">
                <h3 class="text-base font-bold text-gray-900 leading-none mb-0">Structure Capital &amp; Bénéfices</h3>
                <p class="text-xs text-gray-400 mt-0.5">Proportions du capital recouvré, risque et bénéfice</p>
              </div>
            </div>
            <div class="h-80">
              <canvas baseChart [data]="capitalDistributionChart.data" [options]="capitalDistributionChart.options"
                [type]="capitalDistributionChart.type">
              </canvas>
            </div>
          </div>
        </div>

        <!-- ═══ Daily Data Table with Avoir Traceability ═══ -->
        <div *ngIf="hasData()" class="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden mb-12">
          <!-- Table Header -->
          <div class="px-6 py-5 bg-gradient-to-r from-slate-700 to-slate-900 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center border border-white/20">
                <i class="fa fa-table text-white text-sm"></i>
              </div>
              <div>
                <h3 class="text-lg font-bold text-white mb-0">Données Journalières Détaillées</h3>
                <p class="text-slate-300 text-xs mt-0.5">Les jours avec avoirs sont mis en évidence — traçabilité complète</p>
              </div>
            </div>
            <span class="bg-orange-500/20 text-orange-300 border border-orange-500/30 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
              <i class="fa fa-undo text-xs"></i> Avec Avoirs
            </span>
          </div>

          <!-- Scrollable Table -->
          <div class="overflow-x-auto">
            <table class="w-full text-left border-collapse text-sm">
              <thead>
                <tr class="bg-slate-50 border-b-2 border-slate-200">
                  <th class="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Date</th>
                  <th class="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right whitespace-nowrap">CA Brut</th>
                  <th class="px-4 py-3 text-xs font-semibold text-orange-500 uppercase tracking-wider text-center whitespace-nowrap">
                    <i class="fa fa-undo mr-1"></i>Avoirs
                  </th>
                  <th class="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right whitespace-nowrap">CA Net</th>
                  <th class="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right whitespace-nowrap">Bénéfice Net</th>
                  <th class="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right whitespace-nowrap">Capital Payé</th>
                  <th class="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right whitespace-nowrap">Cash Encaissé</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let date of financialReportService.getSortedDates(financialData).slice().reverse()">
                  <ng-container *ngIf="financialData[date] as day">
                    <!-- Main Row — highlighted if avoir present -->
                    <tr [class.bg-orange-50]="day.refundedRevenue && day.refundedRevenue > 0"
                        [class.border-l-4]="day.refundedRevenue && day.refundedRevenue > 0"
                        [class.border-l-orange-400]="day.refundedRevenue && day.refundedRevenue > 0"
                        class="border-b border-slate-100 hover:bg-slate-50 transition-colors">

                      <!-- Date -->
                      <td class="px-4 py-3 font-semibold text-slate-700 whitespace-nowrap">
                        {{ financialReportService.formatDate(date) }}
                        <span *ngIf="day.refundedRevenue && day.refundedRevenue > 0"
                              class="ml-2 inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-orange-200">
                          <i class="fa fa-undo text-[9px]"></i> AVOIR
                        </span>
                      </td>


                      <!-- CA Brut (Cash In before refunds) -->
                      <td class="px-4 py-3 text-right text-slate-700 font-medium whitespace-nowrap">
                        {{ (day.cashTotal + (day.refundedRevenue || 0)) | number:'1.2-2' }}
                      </td>

                      <!-- Avoirs Column -->
                      <td class="px-4 py-3 text-center whitespace-nowrap">
                        <span *ngIf="day.refundedRevenue && day.refundedRevenue > 0"
                              class="inline-flex flex-col items-center">
                          <span class="text-orange-600 font-bold">- {{ day.refundedRevenue | number:'1.2-2' }}</span>
                          <span class="text-[10px] text-orange-400">({{ day.refundedCapital | number:'1.2-2' }} Capital)</span>
                        </span>
                        <span *ngIf="!day.refundedRevenue || day.refundedRevenue === 0"
                              class="text-slate-300 text-xs">—</span>
                      </td>

                      <!-- CA Net (Actual Cash) -->
                      <td class="px-4 py-3 text-right font-semibold whitespace-nowrap"
                          [class.text-orange-700]="day.refundedRevenue && day.refundedRevenue > 0"
                          [class.text-slate-800]="!day.refundedRevenue || day.refundedRevenue === 0">
                        {{ day.cashTotal | number:'1.2-2' }}
                      </td>

                      <!-- Bénéfice Net -->
                      <td class="px-4 py-3 text-right whitespace-nowrap">
                        <span [class.text-emerald-600]="day.paidBenefit > 0"
                              [class.text-red-500]="day.paidBenefit <= 0"
                              class="font-semibold">
                          {{ day.paidBenefit | number:'1.2-2' }}
                        </span>
                        <span *ngIf="day.rawPaidBenefit && day.rawPaidBenefit > day.paidBenefit"
                              class="ml-1 text-[10px] text-orange-400 font-medium"
                              [title]="'Brut avant absorption: ' + (day.rawPaidBenefit | number:'1.2-2')">
                          (brut: {{ day.rawPaidBenefit | number:'1.2-2' }})
                        </span>
                      </td>

                      <!-- Capital Payé -->
                      <td class="px-4 py-3 text-right text-slate-600 whitespace-nowrap">
                        {{ getDirectPaidCapital(day) | number:'1.2-2' }}
                      </td>

                      <!-- Cash Encaissé -->
                      <td class="px-4 py-3 text-right font-bold whitespace-nowrap"
                          [class.text-blue-700]="day.cashTotal > 0"
                          [class.text-slate-400]="day.cashTotal === 0">
                        {{ day.cashTotal | number:'1.2-2' }}
                      </td>
                    </tr>
                  </ng-container>
                </ng-container>
                <!-- Final Summary Total Row -->
                <tr class="bg-slate-100 border-t-2 border-slate-300 font-black">
                  <td class="px-4 py-4 text-slate-900 uppercase tracking-tighter">TOTAL PÉRIODE</td>
                  <td class="px-4 py-4 text-right text-slate-900 whitespace-nowrap">
                    {{ totals.totalRawPaidRevenue | number:'1.2-2' }}
                  </td>
                  <td class="px-4 py-4 text-center text-orange-600 whitespace-nowrap">
                    - {{ totals.totalRefundedRevenue | number:'1.2-2' }}
                  </td>
                  <td class="px-4 py-4 text-right text-indigo-700 whitespace-nowrap text-lg">
                    {{ totals.totalPaidRevenue | number:'1.2-2' }}
                  </td>
                  <td class="px-4 py-4 text-right text-emerald-700 whitespace-nowrap">
                    <span class="text-xl font-bold">{{ totals.totalPaidBenefit | number:'1.2-2' }}</span>
                    <span *ngIf="totals.totalRawPaidBenefit > totals.totalPaidBenefit"
                          class="ml-1 text-xs text-orange-500 font-medium italic">
                      (brut: {{ totals.totalRawPaidBenefit | number:'1.2-2' }})
                    </span>
                  </td>
                  <td class="px-4 py-4 text-right text-slate-700 whitespace-nowrap">
                    {{ totals.totalPaidCapital | number:'1.2-2' }}
                  </td>
                  <td class="px-4 py-4 text-right text-blue-800 whitespace-nowrap bg-blue-50/50">
                    {{ totals.totalCash | number:'1.2-2' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Table Footer: Legend -->
          <div class="px-6 py-3 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center gap-4 text-xs text-slate-500">
            <span class="flex items-center gap-1.5">
              <span class="w-3 h-3 rounded-full bg-orange-400 border border-orange-500"></span>
              Jours avec avoirs — CA Brut inclus le montant avant déduction
            </span>
            <span class="flex items-center gap-1.5">
              <span class="w-3 h-3 rounded-full bg-emerald-400"></span>
              CA Net = après déduction des avoirs
            </span>
            <span class="flex items-center gap-1.5">
              <span class="italic">K = Capital remboursé</span>
            </span>
          </div>
        </div>


        <!-- Business Rules Section -->
        <div *ngIf="hasData()" class="bg-white rounded-xl shadow-lg border border-gray-200 mb-8">
          <div class="px-6 py-5 bg-gradient-to-r from-blue-500 to-indigo-600">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center border-2 border-white/30">
                <i class="fa fa-info-circle text-white text-lg"></i>
              </div>
              <div>
                <h3 class="text-xl font-bold text-white">Règles Métier & Logique</h3>
                <p class="text-blue-100 text-sm">Principes fondamentaux de reporting financier et calculs</p>
              </div>
            </div>
          </div>

          <div class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

              <!-- Benefice Brut & Net -->
              <div
                class="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200 hover:shadow-lg transition-shadow duration-300">
                <div class="flex items-center gap-3 mb-3">
                  <div
                    class="w-10 h-10 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-full flex items-center justify-center border-2 border-green-500/30">
                    <i class="fa fa-chart-bar text-green-600 text-sm"></i>
                  </div>
                  <h4 class="font-semibold text-gray-900">Bénéfice Brut &amp; Bénéfice Net</h4>
                </div>
                <p class="text-sm text-gray-600">
                  <span class="font-medium text-green-700">Brut</span> = profit total généré avant toute
                  compensation.<br>
                  <span class="font-medium text-blue-700">Net</span> = ce qui reste après avoir utilisé une partie du
                  bénéfice
                  pour couvrir les commandes impayées et les prêts.<br>
                  <span class="font-medium text-amber-600">Impayé</span> = bénéfice sur commandes non encore réglées par
                  les
                  clients.
                </p>
              </div>

              <!-- Tresorerie Collectee -->
              <div
                class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200 hover:shadow-lg transition-shadow duration-300">
                <div class="flex items-center gap-3 mb-3">
                  <div
                    class="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-full flex items-center justify-center border-2 border-blue-500/30">
                    <i class="fa fa-money-bill-wave text-blue-600 text-sm"></i>
                  </div>
                  <h4 class="font-semibold text-gray-900">Trésorerie Collectée</h4>
                </div>
                <p class="text-sm text-gray-600">
                  Tout l'argent physiquement reçu sur la période : revenus des commandes intégralement payées + acomptes
                  des commandes partiellement payées.
                </p>
              </div>

              <!-- Creances Clients -->
              <div
                class="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200 hover:shadow-lg transition-shadow duration-300">
                <div class="flex items-center gap-3 mb-3">
                  <div
                    class="w-10 h-10 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-full flex items-center justify-center border-2 border-orange-500/30">
                    <i class="fa fa-file-invoice-dollar text-orange-600 text-sm"></i>
                  </div>
                  <h4 class="font-semibold text-gray-900">Créances Clients</h4>
                </div>
                <p class="text-sm text-gray-600">
                  Le total des revenus encore dus par les clients sur leurs commandes non réglées (capital + bénéfice
                  impayés). Ne comprend pas les prêts.
                </p>
              </div>

              <!-- Capital a Risque -->
              <div
                class="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 border border-red-200 hover:shadow-lg transition-shadow duration-300">
                <div class="flex items-center gap-3 mb-3">
                  <div
                    class="w-10 h-10 bg-gradient-to-br from-red-500/20 to-red-600/20 rounded-full flex items-center justify-center border-2 border-red-500/30">
                    <i class="fa fa-shield-alt text-red-600 text-sm"></i>
                  </div>
                  <h4 class="font-semibold text-gray-900">Capital à Risque</h4>
                </div>
                <p class="text-sm text-gray-600">
                  Montant résiduel non couvert après que le bénéfice a absorbé au maximum les dettes. C'est la perte
                  potentielle réelle si rien n'est recouvré. Comprend commandes + prêts.
                </p>
              </div>

              <!-- Absorption du Benefice -->
              <div
                class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200 hover:shadow-lg transition-shadow duration-300">
                <div class="flex items-center gap-3 mb-3">
                  <div
                    class="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-full flex items-center justify-center border-2 border-purple-500/30">
                    <i class="fa fa-sort-numeric-up text-purple-600 text-sm"></i>
                  </div>
                  <h4 class="font-semibold text-gray-900">Absorption du Bénéfice</h4>
                </div>
                <p class="text-sm text-gray-600">
                  Le bénéfice payé est utilisé pour couvrir les dettes dans cet ordre :<br>
                  <span class="font-medium">1.</span> <span
                    title="Marchandises tracées dans la base de données">Marchandises en stock impayées</span> &nbsp;
                  <span class="font-medium">2.</span> <span
                    title="Petits articles non gérés en stock (ex: vis, clous, etc.)">Marchandises hors stock
                    impayées</span>
                  &nbsp;
                  <span class="font-medium">3.</span> <span title="Argent prêté directement à l'extérieur">Argent prêté
                    (Salaf)</span>
                </p>
              </div>

              <!-- Creances Prets -->
              <div
                class="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 border border-indigo-200 hover:shadow-lg transition-shadow duration-300">
                <div class="flex items-center gap-3 mb-3">
                  <div
                    class="w-10 h-10 bg-gradient-to-br from-indigo-500/20 to-indigo-600/20 rounded-full flex items-center justify-center border-2 border-indigo-500/30">
                    <i class="fa fa-hand-holding-usd text-indigo-600 text-sm"></i>
                  </div>
                  <h4 class="font-semibold text-gray-900">Créances Prêts</h4>
                </div>
                <p class="text-sm text-gray-600">
                  La somme totale brute des prêts (salaf) accordés et non encore remboursés sur la période.
                </p>
              </div>

              <!-- Impaye Brut -->
              <div
                class="bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl p-6 border border-rose-200 hover:shadow-lg transition-shadow duration-300">
                <div class="flex items-center gap-3 mb-3">
                  <div
                    class="w-10 h-10 bg-gradient-to-br from-rose-500/20 to-rose-600/20 rounded-full flex items-center justify-center border-2 border-rose-500/30">
                    <i class="fa fa-exclamation-circle text-rose-600 text-sm"></i>
                  </div>
                  <h4 class="font-semibold text-gray-900">Impayé Brut</h4>
                </div>
                <p class="text-sm text-gray-600">
                  Montant total de toutes les dettes avant que le bénéfice ne les compense : commandes non réglées +
                  services
                  non payés + prêts. C'est le <em>pire scénario</em> si aucune absorption n'est possible.
                </p>
              </div>

              <!-- Capital Recouvre -->
              <div
                class="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 border border-teal-200 hover:shadow-lg transition-shadow duration-300">
                <div class="flex items-center gap-3 mb-3">
                  <div
                    class="w-10 h-10 bg-gradient-to-br from-teal-500/20 to-teal-600/20 rounded-full flex items-center justify-center border-2 border-teal-500/30">
                    <i class="fa fa-check-circle text-teal-600 text-sm"></i>
                  </div>
                  <h4 class="font-semibold text-gray-900">Capital Recouvré</h4>
                </div>
                <p class="text-sm text-gray-600">
                  Montant total du capital (marchandises + services) effectivement encaissé sur la période :
                  paiements directs des clients + part couverte par absorption du bénéfice payé.
                </p>
                <p class="text-[10px] text-teal-600 font-medium mt-1" *ngIf="(totals.totalPaidStockedCapitalByBenefit + totals.totalPaidUnstockedCapitalByBenefit) > 0">
                  Dont {{ (totals.totalPaidStockedCapitalByBenefit + totals.totalPaidUnstockedCapitalByBenefit) | number:'1.2-2' }} MAD par absorption de bénéfice
                </p>
              </div>

              <!-- Capital Engage Non Recouvre -->
              <div
                class="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 border border-amber-200 hover:shadow-lg transition-shadow duration-300">
                <div class="flex items-center gap-3 mb-3">
                  <div
                    class="w-10 h-10 bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-full flex items-center justify-center border-2 border-amber-500/30">
                    <i class="fa fa-hourglass-half text-amber-600 text-sm"></i>
                  </div>
                  <h4 class="font-semibold text-gray-900">Capital Engagé Non Recouvré</h4>
                </div>
                <p class="text-sm text-gray-600">
                  Capital total encore attendu des clients (marchandises + services + prêts non remboursés), après
                  absorption
                  par le bénéfice. C'est ce que le business <em>n'a pas encore récupéré</em>.
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>